import { useLayoutEffect, useMemo, useRef, useSyncExternalStore } from 'react';
import { useAuth } from '@/context/AuthContext';
import { getActiveSessionRevision, getAuthTokenForRequest, subscribeActiveSessionRevision } from '@/api/authenticatedClient';
import { isDesktopOffline } from '@/api/desktopSync';
import { API } from '@/config/constants';
import { resolveBackendUrl } from '@/config/desktopBootstrap';
import { getUsers, createUser, updateUser, disableUser, reactivateUser, resetUserPassword } from '@/api/users';

// TCA-26101815: only non-secret pending identities survive panel retirement.
// Missing tenant evidence conflicts conservatively with every owner at that API.
// This is renderer-lifetime admission, not a durable/server transaction lock.
const pending = new Set();
const observers = new Set();
let sequence = 0;
const overlaps = (a, b) => a.endpoint === b.endpoint && (!a.tenant || !b.tenant || a.tenant === b.tenant);
const text = value => typeof value === 'string' && value.length > 0;
const object = value => value && typeof value === 'object' && !Array.isArray(value);
const connection = () => JSON.stringify([window.lvbDesktop?.backendUrl, window.lvbDesktop?.localBackendUrl]);
const transportMatches = () => {
  const backend = resolveBackendUrl(window.location.protocol, window.lvbDesktop, process.env.REACT_APP_BACKEND_URL);
  return API !== null && backend !== null && `${backend || ''}/api` === API;
};
const principal = binding => JSON.stringify([binding?.user?.id, binding?.user?.tenant_id, binding?.source]);
const rolesFor = user => {
  const values = Object.prototype.hasOwnProperty.call(user || {}, 'roles')
    ? user.roles
    : typeof user?.role === 'string' ? [user.role] : [];
  return Array.isArray(values)
    ? [...new Set(values.filter(role => ['admin', 'driver', 'designer'].includes(role)))]
    : [];
};

const publicUser = value => {
  if (!object(value) || !text(value.id) || !text(value.username) || !text(value.name)) return null;
  if (['color', 'phone_number', 'disabled_at', 'tenant_id'].some(key => value[key] != null && typeof value[key] !== 'string')) return null;
  const result = { id: value.id, username: value.username, name: value.name, roles: [...rolesFor(value)] };
  for (const key of ['color', 'phone_number', 'disabled_at', 'tenant_id']) if (value[key] != null) result[key] = value[key];
  return result;
};
const validDisable = value => object(value) && ['User disabled', 'User already disabled'].includes(value.message)
  && ['orders_released', 'runs_released'].every(key => Number.isSafeInteger(value[key]) && value[key] >= 0);
const refusedResult = error => {
  const status = error?.response?.status;
  const detail = error?.response?.data?.detail;
  // A stale-write 409 may be raised after update/reactivation committed but its
  // readback failed. Only the documented pre-write business conflicts refuse.
  const business = typeof detail === 'string' && detail.length <= 500 && (
    ['Username already exists', 'Username already taken', 'No fields to update', 'User not found',
      'Account is already active', 'A new password is required to reactivate this account',
      'Cannot disable the last active admin'].includes(detail)
    || detail.startsWith('Phone number already assigned to ')
    || /^Cannot disable: assigned to \d+ active runs? with stops still in progress — /.test(detail));
  if ([400, 404, 409].includes(status) && business) return { kind: 'rejected', message: detail };
  if ([401, 403].includes(status)) return { kind: 'rejected', message: 'This session cannot make that account change. Review access before trying again.' };
  if (status === 422) return { kind: 'rejected', message: 'The account change was refused. Check the entered values before trying again.' };
  return { kind: 'unknown', message: 'The account change was not confirmed and may have taken effect. Refresh and review before a deliberate new action. Nothing will be resent automatically; the list cannot verify a password or prove an absent account was not created.' };
};

export function createUserManagementController(authorize, resource) {
  const key = ++sequence;
  const listeners = new Set();
  const reads = new Set();
  let active = false;
  let life = 0;
  let version = 0;
  let includeDisabled = false;
  let snapshot = { users: [], state: 'loading', known: false, pending: false, active: false, writable: false, includeDisabled };
  const owns = (write = false) => active && !!authorize(write);
  const blocked = () => !owns(true) || [...pending].some(attempt => overlaps(resource, attempt));
  const notify = () => {
    snapshot = { ...snapshot, active: owns(), writable: owns(true), pending: [...pending].some(attempt => overlaps(resource, attempt)) };
    listeners.forEach(listener => listener());
  };
  const patch = update => { snapshot = { ...snapshot, ...update }; notify(); };
  const refresh = async () => {
    if (!owns()) return false;
    const started = life; const requestVersion = ++version; const filter = includeDisabled;
    const request = new AbortController();
    const config = authorize();
    const current = () => owns() && life === started && version === requestVersion && !request.signal.aborted;
    reads.add(request); patch({ state: 'loading' });
    try {
      const result = await getUsers(filter ? { include_disabled: true } : undefined, { ...config, signal: request.signal });
      if (!current()) return false;
      if (!Array.isArray(result)) throw new Error('Invalid user directory');
      const users = result.map(publicUser);
      if (users.some(user => !user) || new Set(users.map(user => user.id)).size !== users.length) throw new Error('Invalid user directory');
      patch({ users, state: 'available', known: true });
      return true;
    } catch {
      if (current()) patch({ state: 'unavailable' });
      return false;
    } finally { reads.delete(request); }
  };
  const changed = (attempt, settled) => {
    if (!overlaps(resource, attempt)) return;
    ++version;
    if (snapshot.state === 'loading') snapshot = { ...snapshot, state: 'unavailable' };
    notify();
    // Every live owner acquires its own evidence, even after a retired writer.
    if (settled && owns()) void refresh();
  };
  const publish = (attempt, settled = false) => observers.forEach(observer => observer(attempt, settled));
  const mutate = async (kind, id, data) => {
    if (blocked()) return { kind: 'blocked' };
    const started = life; const config = authorize(true);
    const createdIdentity = kind === 'create' ? { username: data.username, name: data.name, tenant: resource.tenant } : null;
    const current = () => owns(true) && life === started;
    const attempt = { ...resource };
    pending.add(attempt); publish(attempt);
    let result;
    try {
      if (!current()) return { kind: 'retired' };
      let request;
      if (kind === 'create') request = createUser(data, config);
      else if (kind === 'update') request = updateUser(id, data, config);
      else if (kind === 'disable') request = disableUser(id, config);
      else if (kind === 'reactivate') request = reactivateUser(id, data, config);
      else if (kind === 'reset') request = resetUserPassword(id, data, config);
      else throw new Error('Unknown user command');
      data = null; // The already-dispatched transport alone owns its input.
      const value = await request;
      if (!current()) return { kind: 'retired' };
      if (kind === 'disable') {
        if (!validDisable(value)) throw new Error('Unconfirmed disable');
        result = { kind: 'accepted' };
      } else {
        const user = publicUser(value);
        if (!user || (kind !== 'create' && user.id !== id)) throw new Error('Unconfirmed account change');
        if (createdIdentity && (user.username !== createdIdentity.username || user.name !== createdIdentity.name
          || (createdIdentity.tenant && user.tenant_id !== createdIdentity.tenant))) throw new Error('Unconfirmed created account');
        result = { kind: 'accepted', user };
      }
    } catch (error) { result = refusedResult(error); }
    finally {
      data = null;
      pending.delete(attempt); publish(attempt, true);
    }
    return current() ? result : { kind: 'retired' };
  };
  return {
    key, owns, blocked, refresh, mutate,
    getSnapshot: () => snapshot,
    subscribe: listener => { listeners.add(listener); return () => listeners.delete(listener); },
    filter(value) {
      if (!owns() || value === includeDisabled) return;
      includeDisabled = value; ++version;
      patch({ includeDisabled, users: [], known: false }); void refresh();
    },
    activate() { active = true; ++life; observers.add(changed); notify(); if (owns()) void refresh(); },
    retire() { active = false; ++life; ++version; reads.forEach(request => request.abort()); observers.delete(changed); notify(); },
  };
}

export function useUserManagement() {
  const { sessionBinding } = useAuth() || {};
  const revision = useSyncExternalStore(subscribeActiveSessionRevision, getActiveSessionRevision);
  const identity = principal(sessionBinding);
  const source = connection();
  const permitted = rolesFor(sessionBinding?.user).includes('admin');
  const latest = useRef(null);
  latest.current = { sessionBinding, source };
  const controller = useMemo(() => {
    const authorize = (write = false) => {
      const current = latest.current;
      const binding = current.sessionBinding;
      if (!permitted || !binding?.user?.id || principal(binding) !== identity || binding.revision !== revision
        || revision !== getActiveSessionRevision() || !rolesFor(binding.user).includes('admin')
        || source !== current.source || source !== connection() || !transportMatches()
        || (write && (binding.source !== 'cloud' || isDesktopOffline()))) return null;
      const token = getAuthTokenForRequest(`${API}/users`);
      return token ? { headers: { Authorization: `Bearer ${token}` }, timeout: 30000, suppressServerErrorNotification: true } : null;
    };
    return createUserManagementController(authorize, { endpoint: API, tenant: sessionBinding?.user?.tenant_id || null });
  }, [identity, revision, source, permitted, sessionBinding?.user?.tenant_id]);
  useLayoutEffect(() => { controller.activate(); return () => controller.retire(); }, [controller]);
  const snapshot = useSyncExternalStore(controller.subscribe, controller.getSnapshot);
  return { controller, ...snapshot, currentUser: sessionBinding?.user, connectionError: !transportMatches() };
}
